import { AfterContentInit, Component, Inject, OnInit } from '@angular/core';

import { MediaService } from '@core/services/media.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { NotificationService } from '@core/services/notifications.service';

import { ApiResponseModel as ApiResponse } from '@shared/models/services/api.response.model';
import { SfxDialog, DialogResponse } from '@shared/components/dialog/dialog.component';

import { catchError, filter, mergeMap, of } from 'rxjs';

import {
    EditApiKeyContentData, EditApiKeyDialogContentComponent
} from '@feature/settings/components/api-key-settings/dialogs/edit-api-key-dialog-content.component';

import { ApiKeyModel as ApiKey } from '@shared/models/api-key.model';

@Component({
    selector: 'sfx-api-key-settings',
    templateUrl: './api-key-settings.component.html',
    styleUrls: ['./api-key-settings.component.scss']
})
export class ApiKeySettingsComponent implements OnInit, AfterContentInit {

    public apiDocsUrl = '';

    apiKeys: ApiKey[] = [];
    initialLoad = false;

    private $filter: any;

    constructor(
        public media: MediaService,
        private matDialog: MatDialog,
        private notificationService: NotificationService,
        private translate: TranslateService,
        @Inject('apiKeyService') private apiKeyService: any,
        @Inject('apiKeysService') private apiKeysService: any,
        @Inject('config') private config: any,
        @Inject('$filter') $filter: any
    ) {
        this.$filter = $filter;
    }

    formatDate(dateInput: any): string {
        if (!dateInput) {
            return '';
        }

        const formatLongDateFilter = this.$filter('formatAngularLongDate');
        return formatLongDateFilter(dateInput) || '';
    }

    ngOnInit(): void {

        this.apiDocsUrl = this.config.apiUrl + 'docs';

        this.getApiKeys().then(() => {

            this.initialLoad = true;
        });
    }

    ngAfterContentInit(): void {}

    private getApiKeys() {

        return this.apiKeysService.get().then((response: ApiResponse<any[]>) => {

            this.apiKeys = response.data;
        });
    }

    public createApiKey() {

        this.matDialog.open(SfxDialog, {
            panelClass: 'sfx-dialog-container-unthemed',
            data: {
                title: this.translate.instant('Create API Key'),
                confirm: this.translate.instant('Create'),
                close: this.translate.instant('Cancel'),
                context: { apiKey: { name: '' } },
                content: EditApiKeyDialogContentComponent
            }
        }).afterClosed()
            .pipe(
                filter((context: DialogResponse<EditApiKeyContentData>) => context?.confirmed),
                mergeMap((context: DialogResponse<EditApiKeyContentData>) => this.apiKeyService.create(context.state.apiKey)),
                catchError(() => {

                    return of(null);
                })
            )
            .subscribe({
                next: (result) => {

                    if (result) {
                        this.notificationService.success(this.translate.instant('Success! Your API key has been created.'));
                    }

                    this.getApiKeys();
                }
            });
    }

    public editApiKey(apiKey: ApiKey): void {

        this.matDialog.open(SfxDialog, {
            panelClass: 'sfx-dialog-container-unthemed',
            data: {
                title: this.translate.instant('Edit API Key'),
                confirm: this.translate.instant('Save'),
                close: this.translate.instant('Cancel'),
                context: { apiKey },
                content: EditApiKeyDialogContentComponent
            }
        }).afterClosed()
            .pipe(
                filter((context: DialogResponse<EditApiKeyContentData>) => context?.confirmed),
                mergeMap((context: DialogResponse<EditApiKeyContentData>) => this.apiKeyService.update(context.state.apiKey)),
                catchError(() => {

                    return of(null);
                })
            )
            .subscribe({
                next: () => {

                    this.getApiKeys();
                }
            });
    }

    public deleteApiKey(apiKey: ApiKey): void {

        this.matDialog.open(SfxDialog, {
            panelClass: 'sfx-dialog-container-unthemed',
            data: {
                title: this.translate.instant('Are you sure you want to delete this API key?'),
                confirm: this.translate.instant('Yes'),
                close: this.translate.instant('No')
            }
        }).afterClosed()
            .pipe(
                filter((context: DialogResponse<any>) => context?.confirmed),
                mergeMap(() => this.apiKeyService.delete(apiKey)),
                catchError(() => {

                    return of(null);
                })
            )
            .subscribe({
                next: () => {

                    this.getApiKeys();
                }
            });
    }

}
